import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import ProjectAdminFormPaymentPaypal from "./ProjectAdminFormPaymentPaypal";
import SwitchOnOff from "../../common/tools/SwitchOnOff";
import InfoTooltip from "../../utils/InfoTooltip";

import type { JSX } from "react";

import type { Project, UpdatedProject } from "../../../api/ProjectAPI.types";
import type { AppState } from "../../../reducers";

interface StripeConnectButtonProps {
  stripeId: string;
}

function StripeConnectButton({
  stripeId,
}: StripeConnectButtonProps): JSX.Element {
  const { t } = useTranslation("editor-project");

  const stripeClientId = useSelector(
    (state: AppState) => state.architectureReducer.stripe_client_id,
  );

  if (stripeId) {
    return (
      <div className="stripe-connect-button stripe-connect-button-disable">
        <span>{t("stripeConnect")}</span>
      </div>
    );
  }

  return (
    <a
      className="stripe-connect-button"
      href={`https://connect.stripe.com/oauth/authorize?response_type=code&client_id=${stripeClientId}&scope=read_write`}
    >
      <span>{t("stripeConnect")}</span>
    </a>
  );
}

interface ProjectAdminFormPaymentProps {
  project: Project | UpdatedProject;
  setProject: (project: Project | UpdatedProject) => void;
}

function ProjectAdminFormPayment({
  project,
  setProject,
}: ProjectAdminFormPaymentProps): JSX.Element {
  const { t } = useTranslation("editor-project");

  const { stripeKey, stripeId } = useMemo(() => {
    const stripeCreds = project.storefront_project_payment_creds?.find(
      (cred) => cred.payment_provider === "stripe",
    );
    if (stripeCreds) {
      return {
        stripeId: stripeCreds.credentials.stripe_user_id,
        stripeKey: stripeCreds.credentials.stripe_publishable_key,
      };
    }

    return { stripeId: "", stripeKey: "" };
  }, [project]);

  const paypalPaymentCreds = useMemo(
    () =>
      project.storefront_project_payment_creds?.find(
        (cred) => cred.payment_provider === "paypal",
      ),
    [project],
  );

  const handleChangeAllowStripePortal = (value: boolean): void => {
    setProject({
      ...project,
      storefront_project_params: {
        ...project.storefront_project_params,
        allow_stripe_portal: value,
      },
    });
  };

  // TODO: to remove when paypal will be available
  const showPaypalConnect = false;

  return (
    <React.Fragment>
      <div className="editor-form-section">
        <div className="editor-form-translates">
          <div className="editor-form-section-title">{t("stripe")}</div>
          <div className="stripe-connect-container">
            <div className="stripe-connect-body">
              <div className="editor-form-translate">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label>{t("accountId")}</label>
                <input
                  disabled
                  className="editor-form-input-title"
                  type="text"
                  value={stripeId}
                />
              </div>
              <div className="editor-form-translate">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label>{t("apiKey")}</label>
                <input
                  disabled
                  className="editor-form-input-title"
                  type="text"
                  value={stripeKey}
                />
              </div>
            </div>
            <div className="stripe-connect-buttons">
              <StripeConnectButton stripeId={stripeId} />
            </div>
          </div>
        </div>
      </div>
      <div className="editor-form-section section-separator">
        <div className="editor-form-row editor-form-row-inline">
          <div className="editor-form-input">
            <SwitchOnOff
              disabled={!project.storefront_project_payment_creds}
              iconName=""
              name="allow_stripe_portal"
              onChange={handleChangeAllowStripePortal}
              size="sm"
              checked={
                project.storefront_project_params
                  ? project.storefront_project_params.allow_stripe_portal
                  : false
              }
            />
          </div>
          <div className="editor-form-label">{t("allowStripePortal")}</div>
          <InfoTooltip width="500px">
            {t("display_stripe_tooltip")}
            <a
              className="link"
              href={t("stripe_help_url")}
              rel="noopener noreferrer"
              target="_blank"
            >
              {t("editor-platform:moreInfos")}
            </a>
          </InfoTooltip>
        </div>
      </div>
      {showPaypalConnect && (
        <ProjectAdminFormPaymentPaypal
          paypalPaymentCreds={paypalPaymentCreds}
        />
      )}
    </React.Fragment>
  );
}

export default ProjectAdminFormPayment;
